import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { PasswordlessAccessApiService } from 'app/library/passwordless-access/services/services/passwordless-access.api-service';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';
import {
    AddModulePasswordlessAccesses,
    CreateModulePasswordlessAccess,
    CreateModulePasswordlessAccessFailure,
    CreateModulePasswordlessAccessSuccess,
    DeleteModulePasswordlessAccess,
    DeleteModulePasswordlessAccessFailure,
    DeleteModulePasswordlessAccessSuccess,
    FetchModulePasswordlessAccess,
    FetchModulePasswordlessAccessFailure,
    FetchModulePasswordlessAccessSuccess,
    SetModulePasswordlessAccess,
    SetModulePasswordlessAccesses,
    UnsetModulePasswordlessAccess,
    UpdateModulePasswordlessAccess,
    UpdateModulePasswordlessAccessFailure,
    UpdateModulePasswordlessAccessSuccess,
} from '../actions';
import { ModulePasswordlessAccessApiService } from '../services/services/module-passwordless-access.api-service';
import { ModulePasswordlessAccess } from './module-passwordless-access.model';
import { ModulePasswordlessAccessStateModel } from './module-passwordless-access.state-model';

@State<ModulePasswordlessAccessStateModel>({
    name: 'modulePasswordlessAccess',
    defaults: new ModulePasswordlessAccessStateModel(),
})
@Injectable()
export class ModulePasswordlessAccessState extends EntityState<ModulePasswordlessAccess> {
    static sortProperties: string[] = ['name'];

    @Selector()
    static getEntity(state: ModulePasswordlessAccessStateModel, id: string = state.item): ModulePasswordlessAccess {
        if (!state.map) {
            return null;
        }

        return state.map.get(id) || null;
    }

    @Selector()
    static getEntities(state: ModulePasswordlessAccessStateModel): ModulePasswordlessAccess[] {
        if (!state.map) {
            return null;
        }

        const toReturn = Array.from(state.map.values());

        toReturn.sort(MaxBrainUtils.sortCompareFn(ModulePasswordlessAccessState.sortProperties));

        return toReturn;
    }

    constructor(private _modulePasswordlessAccessApiService: ModulePasswordlessAccessApiService, private _passwordlessAccessApiService: PasswordlessAccessApiService) {
        super(null, 30);
    }

    @Action(AddModulePasswordlessAccesses)
    addEntities(ctx: StateContext<ModulePasswordlessAccessStateModel>, action: AddModulePasswordlessAccesses): void {
        this._addEntities(ctx, action);
    }

    @Action(SetModulePasswordlessAccess)
    setEntity(ctx: StateContext<ModulePasswordlessAccessStateModel>, action: SetModulePasswordlessAccess): void {
        this._setEntity(ctx, action, AddModulePasswordlessAccesses);
    }

    @Action(UnsetModulePasswordlessAccess)
    unsetEntity(ctx: StateContext<ModulePasswordlessAccessStateModel>): void {
        this._unsetEntity(ctx);
    }

    @Action(CreateModulePasswordlessAccess)
    createEntity(ctx: StateContext<ModulePasswordlessAccessStateModel>, action: CreateModulePasswordlessAccess): void {
        this._modulePasswordlessAccessApiService.create(action.payload).subscribe(
            (entities) => {
                ctx.dispatch([new AddModulePasswordlessAccesses(entities), new CreateModulePasswordlessAccessSuccess(entities)]);
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new CreateModulePasswordlessAccessFailure({ response }));
            }
        );
    }

    @Action(UpdateModulePasswordlessAccess)
    updateEntity(ctx: StateContext<ModulePasswordlessAccessStateModel>, action: UpdateModulePasswordlessAccess): void {
        this._passwordlessAccessApiService.update(action.payload).subscribe(
            (entities) => {
                ctx.dispatch([new AddModulePasswordlessAccesses(entities), new UpdateModulePasswordlessAccessSuccess(entities)]);
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new UpdateModulePasswordlessAccessFailure());
            }
        );
    }

    @Action(DeleteModulePasswordlessAccess)
    deleteEntity(ctx: StateContext<ModulePasswordlessAccessStateModel>, action: DeleteModulePasswordlessAccess): void {
        this._passwordlessAccessApiService.delete(action.payload).subscribe(
            (entities) => {
                ctx.dispatch(new DeleteModulePasswordlessAccessSuccess(action.payload));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new DeleteModulePasswordlessAccessFailure());
            }
        );
    }

    @Action(FetchModulePasswordlessAccess)
    fetchEntity(ctx: StateContext<ModulePasswordlessAccessStateModel>, action: FetchModulePasswordlessAccess): void {
        this._fetchEntity(ctx, action, FetchModulePasswordlessAccessSuccess, FetchModulePasswordlessAccessFailure, AddModulePasswordlessAccesses, SetModulePasswordlessAccess).then(
            () => {}
        );
    }

    @Action(FetchModulePasswordlessAccessSuccess)
    fetchEntitySuccess(ctx: StateContext<ModulePasswordlessAccessStateModel>, action: FetchModulePasswordlessAccessSuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchModulePasswordlessAccessFailure)
    fetchEntityFailure(ctx: StateContext<ModulePasswordlessAccessStateModel>, action: FetchModulePasswordlessAccessFailure): void {
        this._fetchEntityFailure(ctx, action);
    }

    @Action(SetModulePasswordlessAccesses)
    setEntities(ctx: StateContext<ModulePasswordlessAccessStateModel>, action: SetModulePasswordlessAccesses): void {
        this._setEntities(ctx, action);
    }
}
